import React, { useMemo } from 'react';
import { Star, MessageCircle } from 'lucide-react';
import { ProductReview } from '../../types';

interface ReviewSummaryProps {
    reviews: ProductReview[];
}

export const ReviewSummary: React.FC<ReviewSummaryProps> = ({ reviews }) => {
    const stats = useMemo(() => {
        if (!reviews || reviews.length === 0) return null;

        const totalReviews = reviews.length;
        const totalRating = reviews.reduce((acc, review) => acc + review.rating, 0);
        const averageRating = totalRating / totalReviews;
        
        // Count distribution
        const distribution = [0, 0, 0, 0, 0];
        reviews.forEach(r => {
            if (r.rating >= 1 && r.rating <= 5) {
                distribution[r.rating - 1]++;
            }
        });

        return {
            totalReviews,
            averageRating: averageRating.toFixed(1),
            distribution: distribution.reverse(), // 5 to 1
        };
    }, [reviews]);

    if (!stats) return null;

    return (
        <div className="bg-zinc-950/50 border border-zinc-900 rounded-sm p-6 lg:p-8">
            <h3 className="text-xl font-display font-bold uppercase italic text-white mb-6 flex items-center gap-2">
                <Star className="text-white fill-white drop-shadow-[0_0_8px_rgba(255,255,255,0.4)]" />
                Reseñas de Clientes
            </h3>
            
            <div className="flex flex-col md:flex-row gap-8 items-center md:items-start">
                {/* Global Score */}
                <div className="flex flex-col items-center justify-center min-w-[150px]">
                    <span className="text-6xl font-display font-black text-white leading-none mb-2">
                        {stats.averageRating}
                    </span>
                    <div className="flex gap-1 mb-2">
                        {[1, 2, 3, 4, 5].map(star => (
                            <Star 
                                key={star} 
                                size={16} 
                                className={star <= Math.round(Number(stats.averageRating)) ? "fill-white text-white drop-shadow-[0_0_5px_rgba(255,255,255,0.5)]" : "fill-zinc-800 text-zinc-800"} 
                            />
                        ))}
                    </div>
                    <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest flex items-center gap-1">
                        <MessageCircle size={12} /> {stats.totalReviews} Opiniones
                    </span>
                </div>

                {/* Bars */}
                <div className="flex-grow w-full space-y-2">
                    {stats.distribution.map((count, index) => {
                        const stars = 5 - index;
                        const percentage = stats.totalReviews > 0 ? (count / stats.totalReviews) * 100 : 0;
                        return (
                            <div key={stars} className="flex items-center gap-3 text-sm">
                                <div className="flex items-center gap-1.5 text-zinc-400 font-mono text-[10px] w-12 shrink-0">
                                    <span>{stars}</span>
                                    <Star size={10} className="fill-zinc-600 text-zinc-600" />
                                </div>
                                <div className="flex-grow h-1.5 bg-zinc-900 rounded-full overflow-hidden relative">
                                    <div 
                                        className="absolute top-0 left-0 h-full bg-white transition-all duration-1000 ease-out"
                                        style={{ width: `${percentage}%` }}
                                    />
                                </div>
                                <span className="text-zinc-600 font-mono text-[10px] w-8 text-right shrink-0">{count}</span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
